import flatpickr from 'flatpickr';
import { Russian } from 'flatpickr/dist/l10n/ru.js';
import { vars } from '@/app/config/variables';

const getDate = (date): string => {
	const year = date.getFullYear();
	const day = (date.getDate()).toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const createQueryObject = (query): object => {
	const pairs = query.split('&');
	const result = {};
	pairs.forEach((p) => {
		const pair = p.split('=');
		const key = pair[0];
		const value = decodeURIComponent(pair[1] || '');
		if (result[key]) {
			if( Object.prototype.toString.call( result[key] ) === '[object Array]' ) result[key].push( value );
			else result[key] = [ result[key], value ];
		} else {
			result[key] = value;
		}
	});
	return result;
};

const createURLDate = (dates): string => {
	const cleanURL = clearUrlDate();
	const separator = cleanURL.indexOf('?') > -1 ? '&' : '?';
	return `${cleanURL}${separator}date=${dates}`;
};

const clearUrlDate = (): string => {
	const { pathname, search } = window.location;
	const dateStart = search.indexOf('date=');
	const dateEnd = search.substring(dateStart).indexOf('&');
	const searchResult = `${search.slice(0, dateStart)}${dateEnd > -1 ? search.slice(dateEnd) : ''}`;
	return `${pathname}${searchResult.length > 1 ? searchResult : ''}`;
};

const getPagen = (object) => {
	const keys = Object.keys(object);
	return keys.find((key) => key.match(/PAGEN_\d/));
};

export class WebinarsCalendar {
	private element: HTMLElement;
	private className: string;
	private calendarInstance: flatpickr;
	private currentYear = new Date().getFullYear();
	private cards: Array<HTMLElement> = Array.from(document.querySelectorAll('.card-main'));
	private dates: Array<object>;
	private datesFuture: Array<object>;
	private datesPast: Array<object>;
	private pageURL = window.location;
	private pageQuery = createQueryObject(window.location.search.slice(1));
	private media = window.matchMedia(`(min-width: ${vars.breakpoint.tablet}px)`);
	private chooseOpen = document.querySelector('.webinars-season__choose-open');
	private newDates: string;
	private flatpickrDays: HTMLElement;

	private calendarOptions: object = {
		inline: true,
		locale: Russian,
		static: true,
		monthSelectorType: 'static',
		yearSelectorType: 'static',
		minDate: `${this.currentYear}.01.01`,
		maxDate: `${this.currentYear}.12.31`,
		enable: null,
		nextArrow: '',
		prevArrow: '',
		onDayCreate: null,
		onChange: (list, date) => {
			const activeCards: Array<HTMLElement> = [];
			list.forEach((item) => {
				activeCards.push(...this.cards.filter((card) => card.dataset.date === getDate(item)));
			});
			this.newDates = list.map((item) => getDate(item)).toString(',');
			history.pushState({}, '', createURLDate(this.newDates));
			this.cards.forEach((card) => card.classList.remove('active'));
			activeCards.forEach((card, key) => {
				if (key === 0) card.scrollIntoView();
				console.log(key);
				
				card.classList.add('active');
			});
			this.element.dispatchEvent(new CustomEvent('calendarChange', { bubbles: true, detail: { date } }));
		},
	};

	constructor(element: HTMLElement) {
		this.element = element;
		this.className = this.element.className;
		this.dates = JSON.parse(this.element.dataset.calendarJson);
		
		this.datesFuture = this.dates.filter((date) => new Date() <= new Date(date.date));
		this.datesPast = this.dates.filter((date) => new Date() > new Date(date.date));
		this.calendarOptions.enable = this.dates.map((date) => date.date);
		this.calendarOptions.onDayCreate = (dObj, dStr, fp, dayElem) => {
			const date = getDate(dayElem.dateObj);
			const currentDate = this.dates.find((dateObj) => dateObj.date === date);
			if (currentDate) {
				const { href } = currentDate;
				const currentURL = new URL(`${window.location.origin}${href}`);
				if (this.datesPast.find((dateObj) => dateObj.date === date )) dayElem.classList.add('past');
				if (this.datesFuture.find((dateObj) => dateObj.date === date )) dayElem.classList.add('future');

				const currentQuery = createQueryObject(currentURL.search.slice(1));
				const pageQUery = createQueryObject(this.pageURL.search.slice(1));

				const currentPagen = getPagen(currentQuery);
				const pagePagen = getPagen(pageQUery);
				const pagen = currentPagen || pagePagen;

				if ((currentURL.pathname !== this.pageURL.pathname) || (currentQuery[pagen] !== pageQUery[pagen])) {
					dayElem.innerHTML = `<a href="${currentURL.pathname}?${currentURL.search.slice(1)}&date=${date}">${dayElem.innerHTML}</a>`;
					dayElem.addEventListener('click', (event) => event.stopPropagation());
				}
			}

		};
		this.init();
	}

	private placeCalendar = (): void => {
		const { calendarContainer } = this.calendarInstance;
		if (this.media.matches) {
			calendarContainer.classList.remove('webinars-calendar-mobile');
			this.element.append(calendarContainer);
		}
		else {
			calendarContainer.classList.add('webinars-calendar-mobile');
			this.chooseOpen.after(calendarContainer);
		}
	};

	public init = (): void => {
		this.calendarInstance = flatpickr(this.element, this.calendarOptions);
		this.placeCalendar();
		this.media.addEventListener('change', this.placeCalendar);
		if (this.pageQuery.date) this.setDates(this.pageQuery.date);
		this.flatpickrDays = this.calendarInstance.element.querySelector('.flatpickr-days');
		this.flatpickrDays.addEventListener('click', this.clickHandler);
	};

	public destroy = (): void => {
		const parent: HTMLElement = this.element.closest('.webinars-season__item');
		const flatpickrWrapper: HTMLElement = this.element.closest('.flatpickr-wrapper');
		this.calendarInstance.destroy();
		this.calendarInstance = null;
		flatpickrWrapper.remove();
		this.element = document.createElement('div');
		this.element.className = this.className;
		parent.append(this.element);
	};

	public reInit = (): void => {
		this.destroy();
		this.init();
	};

	public setDates = (dates: Array<string>): void => {
		this.calendarInstance.setDate(dates, true);
	};

	public getDates = (): Array<string> => {
		const days: Array<string> = [];
		this.calendarInstance.days.childNodes.forEach((day) => {
			if (day.classList.contains('selected')) days.push(getDate(day.dateObj));
		});
		return days;
	};

	private clickHandler = ({ target }) => {
		if (target.classList.contains('flatpickr-day') && target.classList.contains('selected')) {
			this.setDates([]);
			history.pushState({}, '', clearUrlDate());
		}
	};
}
